import {
  SESSION_ENV,
  SESSION_SERVER_ENV,
  SESSION_STAGING_ENV,
  SESSION_SERVER_STAGING_ENV,
  SESSION_DEV_ENV,
  SESSION_SERVER_DEV_ENV,
  SESSION_SANDBOX_ENV,
  SESSION_SERVER_SANDBOX_ENV,
} from '../../src/env';
import { AuthenticationManager, Session, createSession, getSessionSingleton } from '@benzinga/session';
import { getRuntimeEnvironment, isBetaBzDomain, runningServerSide } from '@benzinga/utils';

export const getSessionEnvironment = () => {
  const environment = getRuntimeEnvironment();
  if (runningServerSide()) {
    switch (environment) {
      case 'development': return SESSION_SERVER_DEV_ENV;
      case 'staging':     return SESSION_SERVER_STAGING_ENV;
      case 'sandbox':     return SESSION_SERVER_SANDBOX_ENV;
      default:            return SESSION_SERVER_ENV;
    }
  }
  switch (environment) {
    case 'development': return SESSION_DEV_ENV;
    case 'staging':     return isBetaBzDomain() ? SESSION_SANDBOX_ENV : SESSION_STAGING_ENV;
    case 'sandbox':     return SESSION_SANDBOX_ENV;
    default:            return SESSION_ENV;
  }
};

// Browser: one Session per page. Each Session owns an AuthenticationManager that fetches /account/session on
// construction, so a fresh Session per call started dozens of parallel session lookups per page load (#16548).
// Kept here rather than in sessionFactory's globalSession, which any env-less createSession() call can claim first.
let browserSession: Session | null = null;

// Server: a fresh Session per call, so nothing is retained across requests (see the warning below).
export const getGlobalSession = (): Session => {
  if (runningServerSide()) {
    return createSession(getSessionEnvironment());
  }
  browserSession ??= createSession(getSessionEnvironment());
  return browserSession;
};

// WARNING: Use only for client-side / local packages. Do NOT use server-side.
//
// The singleton is stored in a module-level variable in sessionFactory.ts which
// acts as a GC root — it is never collected for the lifetime of the Node.js process.
// Because of this, every manager the singleton creates (QuotesManager, etc.) and all
// the data those managers cache internally (quotes, logos, ticker details, peers) also
// stays in memory forever, accumulating across every server request.
//
// On the client side this is fine: one browser session, one user, bounded data.
// On the server, thousands of requests from different users all feed into the same
// singleton stores with no eviction, causing heap growth that only stops at restart.
//
// createSession() above gives each call its own Session that is GC'd normally once
// the request finishes and nothing holds a reference to it anymore.
export const getGlobalSessionSingleton = (): Session => getSessionSingleton(getSessionEnvironment());

export const loadSession = async (benzinga_token?: string): Promise<Session> => {
  const session = createSession();
  if (benzinga_token) {
    session.getManager(AuthenticationManager).setBenzingaToken(benzinga_token);
    await session.getManager(AuthenticationManager).getAuthSession();
  }
  return session;
};
